import React from 'react';
import { Sparkles, Moon, Sun, Wind, Flame } from 'lucide-react';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardContent } from '../ui/TaraCard';

const SpiritualityCompass = () => {
  // Inner awakening cycle
  const awakeningCycle = [
    { period: 'Now - Mar 2025', phase: 'Preparation', intensity: 'low', color: '#93c5fd' },
    { period: 'Apr 2025 - Dec 2025', phase: 'Awakening', intensity: 'high', color: '#a78bfa' },
    { period: 'Jan 2026 - Jun 2026', phase: 'Integration', intensity: 'medium', color: '#c4b5fd' },
  ];

  // Chakra balance (planets mapped to chakras)
  const chakraBalance = [
    { chakra: 'Crown', planet: 'Jupiter', balance: 82, color: '#a855f7' },
    { chakra: 'Third Eye', planet: 'Moon', balance: 75, color: '#6366f1' },
    { chakra: 'Throat', planet: 'Mercury', balance: 68, color: '#0ea5e9' },
    { chakra: 'Heart', planet: 'Venus', balance: 78, color: '#10b981' },
    { chakra: 'Solar Plexus', planet: 'Sun', balance: 72, color: '#f59e0b' },
    { chakra: 'Sacral', planet: 'Mars', balance: 65, color: '#f97316' },
    { chakra: 'Root', planet: 'Saturn', balance: 70, color: '#ef4444' },
  ];

  // Meditation windows
  const meditationWindows = [
    { time: '5-7 AM', type: 'Deep Meditation', power: 'High', icon: Sun },
    { time: '12-2 PM', type: 'Mindfulness', power: 'Medium', icon: Wind },
    { time: '6-8 PM', type: 'Contemplation', power: 'High', icon: Moon },
  ];

  return (
    <div className="space-y-6">
      {/* Overall Spiritual Energy */}
      <TaraCard variant="cosmic" className="text-center">
        <TaraCardContent className="p-8">
          <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 mb-4">
            <Sparkles className="w-16 h-16 text-purple-600" />
          </div>
          <h3 className="font-serif text-xl font-medium text-stone-900 mb-2">
            Spiritual Phase: Awakening
          </h3>
          <p className="text-sm text-stone-600">
            Ketu + Jupiter favorable
          </p>
        </TaraCardContent>
      </TaraCard>

      {/* Inner Awakening Cycle */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle>Inner Awakening Cycle</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Your spiritual journey timeline
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-3">
            {awakeningCycle.map((cycle) => (
              <div
                key={cycle.period}
                className="p-4 rounded-lg"
                style={{ backgroundColor: `${cycle.color}20`, borderLeft: `4px solid ${cycle.color}` }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-stone-900 text-sm">{cycle.phase}</span>
                  <span className="text-xs font-medium text-stone-600 uppercase">{cycle.intensity}</span>
                </div>
                <div className="text-xs text-stone-600">{cycle.period}</div>
              </div>
            ))}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Chakra Balance */}
      <TaraCard variant="sacred">
        <TaraCardHeader>
          <TaraCardTitle>Chakra Balance Map</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Planets mapped to energy centers
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-3">
            {chakraBalance.map((item) => (
              <div key={item.chakra}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm font-medium text-stone-900">
                      {item.chakra}
                    </span>
                    <span className="text-xs text-stone-500">({item.planet})</span>
                  </div>
                  <span className="text-sm font-medium" style={{ color: item.color }}>
                    {item.balance}%
                  </span>
                </div>
                <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${item.balance}%`, backgroundColor: item.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Meditation Windows */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle>Today's Meditation Windows</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Best times for practice
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-3">
            {meditationWindows.map((window) => {
              const Icon = window.icon;
              return (
                <div
                  key={window.time}
                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-white rounded-lg"
                >
                  <Icon className="w-5 h-5 text-purple-500" />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-stone-900">{window.time}</div>
                    <div className="text-xs text-stone-600">{window.type}</div>
                  </div>
                  <span className="text-xs font-medium text-purple-600">{window.power}</span>
                </div>
              );
            })}
          </div>
        </TaraCardContent>
      </TaraCard>
    </div>
  );
};

export default SpiritualityCompass;